import { html } from 'lit';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { FormGroupValue } from '../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { MalwareScanHelper } from '../helpers/malware.scan.helper';

export const MALWARE_SCAN_ID = 256;

export interface ScanAction {
  action: number;
  then?: number;
}

export interface MalwareScanAction {
  whenInfected: ScanAction;
  whenRootKitAction: ScanAction;
  whenPua: ScanAction;
}

export interface MalwareScanArchivesSettings {
  limitArchiveSize: string;
  maximumArchiveDepth: number;
}

export interface MalwareScanArchives {
  enable: 'on' | false;
  scanEmail: boolean;
  settings?: MalwareScanArchivesSettings;
}

export interface MalwareScanFileTypes {
  types: number;
  extentions: Array<string>;
}

// don't know why, but that's how the console wants the props, 'on' as string OR false as boolean
type MiscellaneousType = 'on' | false;

export interface MalwareScanMiscellaneous {
  ignoreKeyloggers: MiscellaneousType;
  preserveLastAccessTime: MiscellaneousType;
  resumeIfInterrupted: MiscellaneousType;
  runTask: MiscellaneousType;
  scanBoot: MiscellaneousType;
  scanUefi: MiscellaneousType;
  scanCookie: MiscellaneousType;
  scanDetachableVolumes: MiscellaneousType;
  scanMemory: MiscellaneousType;
  scanNetworkFiles: MiscellaneousType;
  scanNewChanged: MiscellaneousType;
  scanPUA: MiscellaneousType;
  scanRegistry: MiscellaneousType;
  scanRootkits: MiscellaneousType;
  shutDownWhenFinished: boolean;
}

export interface MalwareScanSettings {
  action: MalwareScanAction;
  archives: MalwareScanArchives;
  fileTypes: MalwareScanFileTypes;
  miscellaneous: MalwareScanMiscellaneous;
}

export interface ScanExclusion {
  path: string;
  exclusionType: number;
}

export interface MalwareScanExclusions {
  exclusions: Array<ScanExclusion>;
  profile: number;
}

export interface MalwareScanCredentials {
  user: string;
  password: string;
}

export interface MalwareScanTarget {
  targets: Array<string>;
  exclusions: MalwareScanExclusions;
  credentials?: MalwareScanCredentials;
}

export interface MalwareScanSpecifics {
  scanProfile: number;
  scanType: number;
  scanSettings: MalwareScanSettings;
  scanTarget: MalwareScanTarget;
  pauseWhenOnBattery: boolean;
  cpuUsageControl: MalwareScanCpuUsageControl;
}

export interface MalwareScanForm {
  general: FormGroupValue;
  settings: FormGroupValue;
  scanTargetFilesAndFolders: string[];
  exclusionItems: ScanExclusion[];
  credentials?: MalwareScanCredentials;
  userCustomExtensions: Array<string>;
}

export interface MalwareScan {
  settings: {
    path: string | Array<string>;
    taskName: string;
    type: number;
    targets: Array<ProtectedEntity>;
    specifics: MalwareScanSpecifics;
    exclusionsTaskType?: number; // this is only used for memory scan
  };
}

export interface MalwareScanCpuUsageControl {
  enabled: boolean;
  maximumUsage: string;
}

export enum MALWARE_SCAN_MISCELLANEOUS {
  BOOT_SECTORS,
  UEFI,
  REGISTRY,
  ROOTKITS,
  KEYLOGGERS,
  NETWORK_SHARES,
  MEMORY,
  COOKIES,
  NEW_AND_CHANGED_FILES,
  PUA,
  RESUME_SCAN_AFTER_PRODUCT_UPDATE,
  DETACHABLE_VOLUMES,
  PRESERVE_LAST_ACCESS_TIME,
}

export enum WINDOWS_SCAN_TARGET_PATH {
  ALL_USERS_PROFILE = '%ALLUSERSPROFILE%',
  APP_DATA = '%AppData%',
  LOCAL_APP_DATA = '%LOCALAPPDATA%',
  PROGRAM_FILES = '%ProgramFiles%',
  USER_PROFILE = '%USERPROFILE%',
  COMMON_PROGRAM_FILES = '%CommonProgramFiles%',
  COMMON_PROGRAM_FILES_X86 = '%COMMONPROGRAMFILES(X86)%',
  WIN_DIR = '%WinDir%',
  WIN_DIR_SYSTEM32 = '%WINDIR%\\system32',
  PROGRAM_FILES_X86 = '%PROGRAMFILES(X86)%',
  TEMP = '%TEMP%',
  LOCAL_DRIVERS = 'LocalDrives',
  REMOVABLE_DRIVES = 'RemovableDrives',
}

export enum LINUX_SCAN_TARGET_PATH {
  LIB = '/lib',
  ETC = '/etc',
}

export enum ScanTargetColumn {
  PATH = 'path',
}

export interface ScanTargetFilesItem {
  [ScanTargetColumn.PATH]: string;
}

export enum CustomExclusionColumn {
  TYPE = 'exclusionType',
  PATH = 'path',
}

export interface CustomExclusionItem {
  [CustomExclusionColumn.TYPE]: string;
  [CustomExclusionColumn.PATH]: string;
}
